import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  LinearProgress, Chip, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideocamIcon from '@mui/icons-material/Videocam';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import { db, storage } from '../../../firebase/config';
import {
  collection, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

const accent = '#E05500';

const TYPE_META = {
  pdf:        { label: 'PDF',            Icon: PictureAsPdfIcon, color: '#E53935' },
  image:      { label: 'Bild',           Icon: ImageIcon,        color: '#1E88E5' },
  audio:      { label: 'Ljud',           Icon: AudioFileIcon,    color: '#8E24AA' },
  video:      { label: 'Video',          Icon: VideocamIcon,     color: '#00897B' },
  transcript: { label: 'Transkribering', Icon: ArticleIcon,      color: '#F4511E' },
  document:   { label: 'Dokument',       Icon: DescriptionIcon,  color: '#546E7A' },
};

function detectType(file) {
  if (!file) return 'document';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('video/')) return 'video';
  const n = file.name.toLowerCase();
  if (n.endsWith('.vtt') || n.endsWith('.srt') || n.endsWith('.txt') || n.endsWith('.md')) return 'transcript';
  return 'document';
}

function fmtBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Material-rad ───────────────────────────────────────────────────────────────
function MaterialRow({ m, onDelete, onEdit }) {
  const [confirming, setConfirming] = useState(false);
  const [editing,    setEditing]    = useState(false);
  const [editName,   setEditName]   = useState(m.name || '');
  const [editDesc,   setEditDesc]   = useState(m.description || '');
  const [editContent,setEditContent]= useState(m.content || '');
  const [saving,     setSaving]     = useState(false);

  const meta = TYPE_META[m.type] || TYPE_META.document;
  const { Icon } = meta;
  const canEditContent = !m.url; // text/paste-material, not file upload

  const handleSaveEdit = async () => {
    setSaving(true);
    await onEdit(m.id, { name: editName.trim(), description: editDesc.trim(), ...(canEditContent ? { content: editContent } : {}) });
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5, border: `1px solid ${accent}44`, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField size="small" label="Namn" fullWidth value={editName} onChange={e => setEditName(e.target.value)} />
        <TextField size="small" label="Beskrivning / vem är det?" fullWidth value={editDesc} onChange={e => setEditDesc(e.target.value)} multiline minRows={2} />
        {canEditContent && (
          <TextField size="small" label="Innehåll" fullWidth value={editContent} onChange={e => setEditContent(e.target.value)} multiline minRows={4}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setEditing(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Avbryt</Button>
          <Button size="small" variant="contained" onClick={handleSaveEdit} disabled={!editName.trim() || saving}
            sx={{ textTransform: 'none', bgcolor: accent, '&:hover': { bgcolor: '#c44300' } }}>
            {saving ? 'Sparar…' : 'Spara'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5,
      border: '1px solid', borderColor: 'divider',
    }}>
      <Box sx={{ color: meta.color, mt: 0.2, flexShrink: 0 }}>
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: m.description ? 0.4 : 0 }}>
          {m.url ? (
            <Typography
              component="a" href={m.url} target="_blank" rel="noopener noreferrer"
              fontWeight={600} fontSize="0.85rem"
              sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { color: accent, textDecoration: 'underline' } }}
            >
              {m.name}
            </Typography>
          ) : (
            <Typography fontWeight={600} fontSize="0.85rem">{m.name}</Typography>
          )}
          <Chip label={meta.label} size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: meta.color + '22', color: meta.color, fontWeight: 600 }} />
          {m.size > 0 && <Typography fontSize="0.68rem" color="text.disabled">{fmtBytes(m.size)}</Typography>}
        </Box>
        {m.description && (
          <Typography fontSize="0.78rem" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            {m.description}
          </Typography>
        )}
        {m.content && !m.url && (
          <Typography fontSize="0.7rem" color="text.disabled" mt={0.25} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {m.content.slice(0, 140)}…
          </Typography>
        )}
      </Box>
      <Box sx={{ flexShrink: 0, display: 'flex' }}>
        {confirming ? (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" onClick={() => onDelete(m)}
              sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#ef4444', minWidth: 0, px: 1 }}>
              Ja, ta bort
            </Button>
            <Button size="small" onClick={() => setConfirming(false)}
              sx={{ textTransform: 'none', fontSize: '0.7rem', color: 'text.secondary', minWidth: 0, px: 1 }}>
              Avbryt
            </Button>
          </Box>
        ) : (
          <>
            <Tooltip title="Redigera">
              <IconButton size="small" onClick={() => setEditing(true)}
                sx={{ color: 'text.disabled', '&:hover': { color: accent } }}>
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ta bort">
              <IconButton size="small" onClick={() => setConfirming(true)}
                sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

// ── Komponent ──────────────────────────────────────────────────────────────────
export default function WorkshopAIContext({ workshopId, initialBrief, initialBriefVisible = true, isOwner = true }) {
  const [brief,        setBrief]        = useState(initialBrief || '');
  const [briefDirty,   setBriefDirty]   = useState(false);
  const [briefSaved,   setBriefSaved]   = useState(false);
  const [briefVisible, setBriefVisible] = useState(initialBriefVisible);
  const [materials,  setMaterials]  = useState([]);
  const [addOpen,    setAddOpen]    = useState(false);
  const [addTab,     setAddTab]     = useState(0);

  // Fil-upload state
  const [uploadFile,     setUploadFile]     = useState(null);
  const [uploadName,     setUploadName]     = useState('');
  const [uploadDesc,     setUploadDesc]     = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading,      setUploading]      = useState(false);
  const [uploadError,    setUploadError]    = useState('');

  // Inklistrings-state
  const [pasteText, setPasteText] = useState('');
  const [pasteDesc, setPasteDesc] = useState('');
  const [pasteName, setPasteName] = useState('');
  const [savingPaste, setSavingPaste] = useState(false);

  const fileInputRef = useRef(null);

  // Lyssna på material-subkollektion
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'workshops', workshopId, 'contextMaterials'),
      snap => setMaterials(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))
      )
    );
    return unsub;
  }, [workshopId]);

  // Spara brief på blur
  const handleBriefBlur = () => {
    if (!briefDirty) return;
    updateDoc(doc(db, 'workshops', workshopId), { 'aiContext.brief': brief })
      .then(() => { setBriefDirty(false); setBriefSaved(true); setTimeout(() => setBriefSaved(false), 2000); });
  };

  // Filval
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    setUploadName(file.name.replace(/\.[^/.]+$/, ''));
    setUploadError('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setUploadFile(file);
    setUploadName(file.name.replace(/\.[^/.]+$/, ''));
    setUploadError('');
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) return;
    setUploading(true);
    setUploadError('');
    const type = detectType(uploadFile);
    const isSmallText = (type === 'transcript' || uploadFile.type.startsWith('text/')) && uploadFile.size < 2 * 1024 * 1024;

    try {
      if (isSmallText) {
        const content = await uploadFile.text();
        await addDoc(collection(db, 'workshops', workshopId, 'contextMaterials'), {
          name: uploadName.trim(), description: uploadDesc.trim(),
          type, content, size: uploadFile.size, mimeType: uploadFile.type,
          createdAt: serverTimestamp(),
        });
      } else {
        const storePath = `workshops/${workshopId}/materials/${Date.now()}_${uploadFile.name}`;
        const storageRef = ref(storage, storePath);
        const task = uploadBytesResumable(storageRef, uploadFile);
        await new Promise((res, rej) => task.on('state_changed',
          s => setUploadProgress(Math.round(s.bytesTransferred / s.totalBytes * 100)),
          rej, res,
        ));
        const url = await getDownloadURL(storageRef);
        await addDoc(collection(db, 'workshops', workshopId, 'contextMaterials'), {
          name: uploadName.trim(), description: uploadDesc.trim(),
          type, url, storagePath: storePath,
          size: uploadFile.size, mimeType: uploadFile.type,
          createdAt: serverTimestamp(),
        });
      }
      closeAdd();
    } catch (err) {
      setUploadError(`Uppladdning misslyckades: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSavePaste = async () => {
    if (!pasteText.trim() || !pasteName.trim()) return;
    setSavingPaste(true);
    try {
      await addDoc(collection(db, 'workshops', workshopId, 'contextMaterials'), {
        name: pasteName.trim(), description: pasteDesc.trim(),
        type: 'transcript', content: pasteText.trim(),
        createdAt: serverTimestamp(),
      });
      closeAdd();
    } finally {
      setSavingPaste(false);
    }
  };

  const handleDelete = async (m) => {
    if (m.storagePath) {
      try { await deleteObject(ref(storage, m.storagePath)); } catch {}
    }
    await deleteDoc(doc(db, 'workshops', workshopId, 'contextMaterials', m.id));
  };

  const handleEdit = async (id, fields) => {
    await updateDoc(doc(db, 'workshops', workshopId, 'contextMaterials', id), fields);
  };

  const toggleBriefVisible = async () => {
    const next = !briefVisible;
    setBriefVisible(next);
    await updateDoc(doc(db, 'workshops', workshopId), { 'aiContext.briefVisible': next });
  };

  const closeAdd = () => {
    if (uploading) return;
    setAddOpen(false);
    setUploadFile(null); setUploadName(''); setUploadDesc('');
    setUploadError(''); setUploadProgress(0);
    setPasteText(''); setPasteDesc(''); setPasteName('');
  };

  const wordCount = pasteText.split(/\s+/).filter(Boolean).length;

  return (
    <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>

      {/* Rubrik */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box sx={{ width: 4, height: 24, bgcolor: accent, borderRadius: 1, flexShrink: 0 }} />
        <Typography fontWeight={700} fontSize="1rem">AI-kontext</Typography>
        <Typography fontSize="0.75rem" color="text.disabled">
          Ger AIn bakgrund vid analys och sammanfattning
        </Typography>
      </Box>

      {/* Brief */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" letterSpacing={0.5} sx={{ flex: 1 }}>
          FASILITATORBRIEF
        </Typography>
        {isOwner && (
          <Tooltip
            title={briefVisible
              ? 'Briefen är synlig för alla som har tillgång till workshopen. Klicka för att göra den privat — bara du ser innehållet.'
              : 'Briefen är privat — bara du ser innehållet. Klicka för att göra den synlig för övriga.'}
            placement="left"
            arrow
          >
            <Box
              onClick={toggleBriefVisible}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1, py: 0.35, borderRadius: 1, cursor: 'pointer',
                border: '1px solid',
                borderColor: briefVisible ? 'divider' : accent + '66',
                bgcolor: briefVisible ? 'transparent' : accent + '11',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: accent + '18', borderColor: accent + '88' },
              }}
            >
              {briefVisible
                ? <LockOpenIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                : <LockIcon sx={{ fontSize: 13, color: accent }} />}
              <Typography fontSize="0.65rem" fontWeight={600} color={briefVisible ? 'text.disabled' : accent}>
                {briefVisible ? 'Synlig för alla' : 'Privat brief'}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
      {isOwner && (
        <Typography fontSize="0.68rem" color="text.disabled" mb={0.75} sx={{ lineHeight: 1.4 }}>
          {briefVisible
            ? 'Alla med tillgång till workshopen kan läsa briefen. Lås den för att hålla den privat.'
            : 'Briefen är dold för övriga — bara du ser innehållet nedan.'}
        </Typography>
      )}
      {!briefVisible && !isOwner ? (
        <Box sx={{
          minHeight: 72, borderRadius: 1, border: '1px solid', borderColor: 'divider',
          background: 'repeating-linear-gradient(45deg, transparent, transparent 6px, rgba(255,255,255,0.03) 6px, rgba(255,255,255,0.03) 12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5,
        }}>
          <Typography fontSize="0.75rem" color="text.disabled" fontStyle="italic">
            Fasilitatorns brief är inte synlig för övriga deltagare
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }}>
          <TextField
            multiline minRows={3} maxRows={10} fullWidth
            placeholder="Beskriv syftet med workshopen, deltagarnas bakgrund och roll, kontext ni jobbar i och vad ni hoppas att AIn ska hjälpa er med. Ju mer kontext desto bättre analys."
            value={brief}
            onChange={e => { setBrief(e.target.value); setBriefDirty(true); setBriefSaved(false); }}
            onBlur={handleBriefBlur}
            variant="outlined" size="small"
            sx={{ mb: 0.5, '& .MuiOutlinedInput-root': { fontSize: '0.88rem' } }}
          />
          {briefSaved && (
            <Typography fontSize="0.68rem" color="success.main" sx={{ position: 'absolute', bottom: 10, right: 12 }}>
              Sparat
            </Typography>
          )}
          {briefDirty && !briefSaved && (
            <Typography fontSize="0.68rem" color="text.disabled" sx={{ position: 'absolute', bottom: 10, right: 12 }}>
              Osparad — klicka utanför för att spara
            </Typography>
          )}
        </Box>
      )}

      {/* Material-header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 1.5 }}>
        <Typography fontSize="0.72rem" fontWeight={700} color="text.secondary" letterSpacing={0.5}>
          KONTEXTMATERIAL
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small" startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
          variant="outlined"
          sx={{ textTransform: 'none', fontSize: '0.78rem', color: accent, borderColor: accent, '&:hover': { bgcolor: accent + '11' } }}
        >
          Lägg till
        </Button>
      </Box>

      {/* Material-lista */}
      {materials.length === 0 ? (
        <Typography fontSize="0.82rem" color="text.disabled" fontStyle="italic" py={0.5}>
          Inga material tillagda. Ladda upp PDF, bilder, ljud/video eller klistra in en transkribering.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {materials.map(m => (
            <MaterialRow key={m.id} m={m} onDelete={handleDelete} onEdit={handleEdit} />
          ))}
        </Box>
      )}

      {/* Lägg till-dialog */}
      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>Lägg till kontextmaterial</DialogTitle>
        <DialogContent>
          <Tabs value={addTab} onChange={(_, v) => setAddTab(v)} sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Ladda upp fil" sx={{ textTransform: 'none', fontSize: '0.82rem' }} />
            <Tab label="Klistra in text / transkribering" sx={{ textTransform: 'none', fontSize: '0.82rem' }} />
          </Tabs>

          {/* ── Tab: Fil-uppladdning ──────────────────────────────── */}
          {addTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: uploadFile ? accent : 'divider',
                  borderRadius: 2, p: 3,
                  textAlign: 'center',
                  cursor: uploading ? 'default' : 'pointer',
                  bgcolor: uploadFile ? accent + '08' : 'action.hover',
                  transition: 'border-color 0.2s, background 0.2s',
                  '&:hover': { borderColor: accent, bgcolor: accent + '08' },
                }}
              >
                <input
                  ref={fileInputRef} type="file" hidden
                  accept="application/pdf,image/*,audio/*,video/*,.txt,.vtt,.srt,.md,.docx"
                  onChange={handleFileChange}
                />
                {uploadFile ? (
                  <Box>
                    <Typography fontSize="0.88rem" fontWeight={600} color={accent}>{uploadFile.name}</Typography>
                    <Typography fontSize="0.72rem" color="text.disabled" mt={0.3}>{fmtBytes(uploadFile.size)}</Typography>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ color: 'text.disabled', fontSize: 32, mb: 0.5 }} />
                    <Typography fontSize="0.85rem" color="text.secondary">Dra hit eller klicka för att välja fil</Typography>
                    <Typography fontSize="0.7rem" color="text.disabled" mt={0.5}>
                      PDF · Bild · Ljud · Video · Transkribering (.txt .vtt .srt)
                    </Typography>
                  </Box>
                )}
              </Box>

              {uploadFile && uploadFile.size > 50 * 1024 * 1024 && (
                <Typography fontSize="0.78rem" color="warning.main">
                  Stor fil ({fmtBytes(uploadFile.size)}). Överväg att använda transkriberingen istället för video/ljud om möjligt.
                </Typography>
              )}

              <TextField
                label="Namn *" value={uploadName} onChange={e => setUploadName(e.target.value)}
                fullWidth size="small" disabled={uploading}
                placeholder="t.ex. Kundintervju med Per Johansson"
              />
              <TextField
                label="Vem är det? Vad handlar det om?"
                value={uploadDesc} onChange={e => setUploadDesc(e.target.value)}
                multiline rows={2} fullWidth size="small" disabled={uploading}
                placeholder="Beskriv vem som talar/syns, varför materialet är relevant för workshopen."
              />

              {uploading && (
                <Box>
                  <LinearProgress variant="determinate" value={uploadProgress}
                    sx={{ mb: 0.5, '& .MuiLinearProgress-bar': { bgcolor: accent } }} />
                  <Typography fontSize="0.72rem" color="text.secondary">Laddar upp… {uploadProgress}%</Typography>
                </Box>
              )}
              {uploadError && <Typography fontSize="0.78rem" color="error">{uploadError}</Typography>}
            </Box>
          )}

          {/* ── Tab: Klistra in text ──────────────────────────────── */}
          {addTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography fontSize="0.82rem" color="text.secondary">
                Klistra in en transkribering från Teams, Zoom eller liknande — eller skriv fri text som ger AIn kontext.
              </Typography>
              <TextField
                label="Namn *" value={pasteName} onChange={e => setPasteName(e.target.value)}
                fullWidth size="small"
                placeholder="t.ex. Kundintervju 2026-04-14 — Per Johansson, VD Exempelföretaget"
              />
              <TextField
                label="Vem är det? Vad handlar det om?"
                value={pasteDesc} onChange={e => setPasteDesc(e.target.value)}
                fullWidth size="small"
                placeholder="Beskriv vem som talar och varför det är relevant för workshopen."
              />
              <TextField
                label="Transkribering / text *"
                value={pasteText} onChange={e => setPasteText(e.target.value)}
                multiline rows={9} fullWidth size="small"
                placeholder="Klistra in text här…"
                sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.82rem', lineHeight: 1.5 } }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography fontSize="0.7rem" color="text.disabled">{pasteText.length} tecken</Typography>
                <Typography fontSize="0.7rem" color="text.disabled">{wordCount} ord</Typography>
                {wordCount > 0 && (
                  <Typography fontSize="0.7rem" color="text.disabled">~{Math.ceil(wordCount / 130)} min läsning</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeAdd} disabled={uploading || savingPaste}
            sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Avbryt
          </Button>
          {addTab === 0 ? (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!uploadFile || !uploadName.trim() || uploading}
              startIcon={uploading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
              sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44300' }, textTransform: 'none', fontWeight: 600 }}
            >
              {uploading ? 'Laddar upp…' : 'Spara'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSavePaste}
              disabled={!pasteText.trim() || !pasteName.trim() || savingPaste}
              startIcon={savingPaste ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
              sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44300' }, textTransform: 'none', fontWeight: 600 }}
            >
              {savingPaste ? 'Sparar…' : 'Spara'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
